/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ComponentType, forwardRef } from 'react';

import { jsx } from '@compiled/react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next/src';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import HelpIcon from '@atlaskit/icon/core/question-circle';

import { BadgeContainer } from '../../components/badge-container';
import { ListItem } from '../../components/list-item';

const HELP_NOTIFICATION_BADGE_ID = 'atlassian-navigation-help-notification-count';

const styles = cssMap({
	hiddenForSmallViewports: {
		display: 'none',
		'@media (min-width: 48rem)': {
			display: 'initial',
		},
	},
});

type HelpProps = {
	/* The component to render as the badge */
	badge?: ComponentType;

	/* Whether the help button is selected (highlights the button when the help panel is open) */
	isSelected?: boolean;

	/* The label for the help button */
	label?: string;

	/* The click handler for the help button (expected to open the help panel) */
	onClick?: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;

	testId?: string;
};

/**
 * __Help__
 *
 * The help button for the top navigation.
 */
export const Help = forwardRef<HTMLButtonElement, HelpProps>(
	({ badge, onClick, label, isSelected, testId }: HelpProps, forwardedRef) => {
		const button = (
			<IconButton
				icon={HelpIcon}
				label={label}
				onClick={onClick}
				appearance="subtle"
				isSelected={isSelected}
				ref={forwardedRef}
				isTooltipDisabled={false}
				testId={testId}
			/>
		);

		if (!badge) {
			return <ListItem xcss={styles.hiddenForSmallViewports}>{button}</ListItem>;
		}

		return (
			<BadgeContainer
				id={HELP_NOTIFICATION_BADGE_ID}
				badge={badge}
				role="listitem"
				xcss={styles.hiddenForSmallViewports}
			>
				{button}
			</BadgeContainer>
		);
	},
);
